import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import {
  type DatabaseShardDirectoryHost,
  type DatabaseShardDirectoryLoaded,
  databaseShardDirectoryPartDocIds,
  DatabaseShardDirectoryPartsMissingError,
  type DatabaseShardDirectoryStoredHead,
  type DatabaseShardDirectoryWindowRead,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
  isOpenDatabaseShardDirectoryPart,
  readDatabaseShardDirectory,
  readDatabaseShardDirectoryHead,
  readDatabaseShardDirectoryLoaded,
  readDatabaseShardDirectoryLoadedWindow,
} from '@streamlain/database-identity';

import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';

/**
 * Directory part documents of a database data source. The block stores the
 * head of its row-shard directory; the shard entries live in part documents
 * of the workspace. Readers of a row window load only the parts holding that
 * window ({@link getShardDirectoryWindow}); readers of the complete list load
 * all parts through {@link getCompleteShardDirectory}.
 */

/** Every database stores a directory head; a database without one is broken. */
export function missingShardDirectoryError(
  databaseId: string
): BlockSuiteError {
  return new BlockSuiteError(
    ErrorCode.DatabaseBlockError,
    `Database "${databaseId}" has no valid row-shard manifest.`
  );
}

/** Head of the stored directory of `host`, its open last part by id. */
export function requireShardDirectoryHead(
  host: DatabaseShardDirectoryHost,
  databaseId: string
): DatabaseShardDirectoryStoredHead {
  const head = readDatabaseShardDirectoryHead(host);
  if (!head) throw missingShardDirectoryError(databaseId);
  return head;
}

/** Directory of `host` as far as its parts are loaded, without requesting any. */
export function requireLoadedShardDirectory(
  host: DatabaseShardDirectoryHost,
  databaseId: string
): DatabaseShardDirectoryLoaded {
  const loaded = readDatabaseShardDirectoryLoaded(host);
  if (!loaded) throw missingShardDirectoryError(databaseId);
  return loaded;
}

/**
 * Complete directory of `host`; throws
 * `DatabaseShardDirectoryPartsMissingError` while parts are not loaded.
 */
export function requireCompleteShardDirectory(
  host: DatabaseShardDirectoryHost,
  databaseId: string
): DatabaseShardManifest {
  const manifest = readDatabaseShardDirectory(host);
  if (!manifest) throw missingShardDirectoryError(databaseId);
  return manifest;
}

export type DatabaseShardDirectoryCompleteRead =
  | { status: 'ready'; manifest: DatabaseShardManifest }
  | { status: 'loading'; partDocIds: string[] }
  /** The server confirmed it holds none of `partDocIds`: the list never completes. */
  | { status: 'missing'; partDocIds: string[] };

/** How a wait for the complete directory ends. */
export type DatabaseShardDirectorySettledRead =
  | { status: 'ready'; manifest: DatabaseShardManifest }
  | { status: 'missing' };

/** The reads of a database data source that tell when its directory is complete. */
export type DatabaseShardDirectoryCompleteSource = {
  getCompleteShardDirectory: () =>
    | DatabaseShardDirectorySettledRead
    | { status: 'loading' };
  /** Calls `onChange` after each later change of rows or directory parts. */
  onShardedRowsChange: (onChange: () => void) => () => void;
};

/**
 * Calls `onSettled` once the complete directory of `source` is there or the
 * server confirmed a part of it missing, right away when that is already so;
 * returns the stop of the wait.
 */
export function onShardDirectoryComplete(
  source: DatabaseShardDirectoryCompleteSource,
  onSettled: (directory: DatabaseShardDirectorySettledRead) => void
): () => void {
  let done = false;
  let stop: (() => void) | undefined;
  const inspect = () => {
    if (done) return;
    const directory = source.getCompleteShardDirectory();
    if (directory.status === 'loading') return;
    done = true;
    stop?.();
    onSettled(directory);
  };
  inspect();
  if (!done) stop = source.onShardedRowsChange(inspect);
  return () => {
    done = true;
    stop?.();
  };
}

function bumpDirectoryRevision(runtime: DatabaseSourceRuntime): void {
  // Part documents may report while a render reads readiness.
  queueMicrotask(() => {
    if (!runtime._disposed) runtime._shardedRowsRevision.value += 1;
  });
}

/** Loads and observes the part documents `partDocIds`, in this order. */
function ensureDirectoryPartDocs(
  runtime: DatabaseSourceRuntime,
  partDocIds: readonly string[]
): void {
  let observerAdded = false;
  for (const partDocId of partDocIds) {
    if (runtime._shardedObservedDocs.has(partDocId)) continue;
    runtime.requestRowShardDocLoad(partDocId, 'directory');
    const doc = runtime.ensureWorkspaceDoc(partDocId);
    if (!doc)
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Cannot load database directory part document "${partDocId}"`
      );
    runtime._shardedObservedDocs.add(partDocId);
    observerAdded = true;
    const refresh = () => {
      bumpDirectoryRevision(runtime);
      runtime.queueShardedRowsRefresh();
    };
    doc.spaceDoc.on('update', refresh);
    runtime._shardedDocObserverDisposers.set(partDocId, () =>
      doc.spaceDoc.off('update', refresh)
    );
    runtime.observeShardDocReady(doc);
  }
  if (observerAdded) {
    bumpDirectoryRevision(runtime);
    runtime.queueShardedRowsRefresh();
  }
}

/**
 * The shards of `shards`, whose first starts at row `rowOffset`, that hold
 * rows of `[offset, offset + limit)`.
 */
export function selectShardsForRowsWindow(
  shards: readonly DatabaseShardManifestEntry[],
  rowOffset: number,
  offset: number,
  limit: number
): DatabaseShardManifestEntry[] {
  const endOffset = offset + limit;
  let shardStart = rowOffset;
  const selected: DatabaseShardManifestEntry[] = [];
  for (const shard of shards) {
    const shardEnd = shardStart + shard.rowCount;
    if (shardEnd > offset && shardStart < endOffset) {
      selected.push(shard);
    }
    if (shardStart >= endOffset) break;
    shardStart = shardEnd;
  }
  return selected;
}

function startsBeforeWindowEnd(rowStart: number, windowEnd: number): boolean {
  return rowStart < windowEnd;
}

/**
 * Head parts starting before `windowEnd`, including empty parts; the open
 * last part starts after the rows of the closed ones.
 */
function shardDirectoryPartsBeforeWindowEnd(
  head: DatabaseShardDirectoryStoredHead,
  windowEnd: number
): DatabaseShardDirectoryStoredHead['parts'] {
  let rowStart = 0;
  return head.parts.filter(part => {
    const beforeEnd = startsBeforeWindowEnd(rowStart, windowEnd);
    if (!isOpenDatabaseShardDirectoryPart(part)) rowStart += part.rowCount;
    return beforeEnd;
  });
}

/**
 * Rows of the database: the closed parts the head counts and the open last
 * part. While the open part is on its way, which it requests, a writer
 * placing rows by position waits for it.
 */
export function requireShardDirectoryRowCount(
  runtime: DatabaseSourceRuntime
): number {
  const loaded = requireLoadedShardDirectory(
    runtime._model,
    runtime.databaseId
  );
  if (loaded.totalRowCount !== undefined) return loaded.totalRowCount;
  const open = loaded.missingParts.slice(-1).map(part => part.partDocId);
  ensureDirectoryPartDocs(runtime, open);
  throw new DatabaseShardDirectoryPartsMissingError(runtime.databaseId, open);
}

/**
 * Rows a reader can show: the database's row count, or, while the open last
 * part is on its way, which it requests, the rows of the closed parts before
 * it, as a row window shows meanwhile.
 */
export function shownShardDirectoryRowCount(
  runtime: DatabaseSourceRuntime
): number {
  const loaded = requireLoadedShardDirectory(
    runtime._model,
    runtime.databaseId
  );
  // The open last part counts its rows itself and changes without the root:
  // the count follows its document, loaded or not.
  const open = loaded.missingParts.at(-1) ?? loaded.loadedParts.at(-1);
  // A new database names no part yet and holds no row.
  if (!open) return 0;
  ensureDirectoryPartDocs(runtime, [open.partDocId]);
  return loaded.totalRowCount ?? open.rowStart;
}

/**
 * Rows as far as the loaded directory counts them, without requesting a
 * part: the database's row count, or, while the open last part is missing,
 * the rows of the closed parts. A row window shows it while its own parts
 * load.
 */
export function loadedShardDirectoryRowCount(
  runtime: DatabaseSourceRuntime
): number {
  const loaded = requireLoadedShardDirectory(
    runtime._model,
    runtime.databaseId
  );
  return loaded.totalRowCount ?? loaded.missingParts.at(-1)!.rowStart;
}

/** Parts of `loaded` still to load that hold rows before `windowEnd`. */
export function missingShardDirectoryWindowParts(
  loaded: DatabaseShardDirectoryLoaded,
  windowEnd: number
): string[] {
  return loaded.missingParts
    .filter(part => startsBeforeWindowEnd(part.rowStart, windowEnd))
    .map(part => part.partDocId);
}

/**
 * Directory of the database as far as its parts are loaded from the first
 * part on. Requests only the parts holding rows before `windowEnd`; the
 * others load once a reader needs the complete list.
 */
export function getLoadedShardDirectory(
  this: DatabaseSourceRuntime,
  windowEnd: number
): DatabaseShardDirectoryLoaded {
  const loaded = requireLoadedShardDirectory(this._model, this.databaseId);
  const head = requireShardDirectoryHead(this._model, this.databaseId);
  const parts = shardDirectoryPartsBeforeWindowEnd(head, windowEnd);
  ensureDirectoryPartDocs(this, databaseShardDirectoryPartDocIds(head, parts));
  return loaded;
}

/**
 * Shards of the directory parts holding the rows `[offset, offset + limit)`,
 * with the rows before the first of them, or loading while those parts are
 * on their way, which it requests. No other part is read or requested.
 */
export function getShardDirectoryWindow(
  this: DatabaseSourceRuntime,
  offset: number,
  limit: number
): DatabaseShardDirectoryWindowRead {
  const loaded = requireLoadedShardDirectory(this._model, this.databaseId);
  const window = readDatabaseShardDirectoryLoadedWindow(loaded, {
    offset,
    limit,
  });
  // The window observes the parts it reads; while loading, it requests them.
  ensureDirectoryPartDocs(
    this,
    window.status === 'loading'
      ? window.partDocIds
      : loaded.loadedParts
          .filter(part =>
            part.shards.some(shard => window.shards.includes(shard))
          )
          .map(part => part.partDocId)
  );
  return window;
}

/**
 * The one check of every reader that needs the complete directory: ready
 * with the complete list, loading while parts are on their way, which it
 * requests, or missing once the server confirmed it does not hold a part.
 * Never an empty or partial list.
 */
export function getCompleteShardDirectory(
  this: DatabaseSourceRuntime
): DatabaseShardDirectoryCompleteRead {
  const loaded = requireLoadedShardDirectory(this._model, this.databaseId);
  const head = requireShardDirectoryHead(this._model, this.databaseId);
  ensureDirectoryPartDocs(this, databaseShardDirectoryPartDocIds(head));
  if (!loaded.missingParts.length)
    return { status: 'ready', manifest: loaded.manifest };
  const partDocIds = loaded.missingParts.map(part => part.partDocId);
  const missing = partDocIds.filter(docId =>
    this._missingRowShardDocIds.has(docId)
  );
  return missing.length
    ? { status: 'missing', partDocIds: missing }
    : { status: 'loading', partDocIds };
}
